import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { FileText, Printer, Filter, Calendar, ChevronLeft, ChevronRight, Lock } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { useAuthStore } from '../../stores/authStore'
import filtrosBateriasService from '../../services/filtrosBateriasService'
import leiloesService from '../../services/leiloesService'

const PER_PAGE = 20

export default function Justificativas() {
  const { organizadorAtivo } = useOrganizador()
  const { user } = useAuthStore()
  const [leilaoFiltro, setLeilaoFiltro] = useState('')
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [page, setPage] = useState(1)

  const isPremium = (organizadorAtivo?.produtos_premium || []).includes('filtros_baterias')

  const { data: leiloes = [] } = useQuery(
    ['leiloes', organizadorAtivo?.id],
    () => leiloesService.getByOrganizador(organizadorAtivo.id),
    { enabled: !!organizadorAtivo?.id }
  )

  const params = {
    page,
    per_page: PER_PAGE,
    ...(leilaoFiltro && { leilao_id: leilaoFiltro }),
    ...(dataInicio && { data_inicio: dataInicio }),
    ...(dataFim && { data_fim: dataFim }),
  }

  const { data: resultado, isLoading } = useQuery(
    ['justificativas', organizadorAtivo?.id, params],
    () => filtrosBateriasService.listarJustificativasOrg(organizadorAtivo.id, params),
    { enabled: !!organizadorAtivo?.id && isPremium, keepPreviousData: true }
  )

  const items = resultado?.items || []
  const total = resultado?.total || 0
  const pages = resultado?.pages || 1

  const handleImprimir = () => {
    const conteudo = `
      <html>
      <head>
        <title>Justificativas de Movimentação - Ordem de Entrada</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; color: #222; }
          h1 { font-size: 16px; margin-bottom: 4px; }
          .sub { color: #666; font-size: 11px; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #f3f4f6; padding: 8px 10px; text-align: left; border-bottom: 2px solid #e5e7eb; font-size: 11px; }
          td { padding: 8px 10px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
          tr:hover { background: #fafafa; }
          .pos { color: #6b7280; font-size: 11px; }
        </style>
      </head>
      <body>
        <h1>Justificativas de Movimentação — Ordem de Entrada</h1>
        <div class="sub">${organizadorAtivo?.name || ''} · Exportado em ${new Date().toLocaleString('pt-BR')}</div>
        <table>
          <thead>
            <tr>
              <th>Data/Hora</th>
              <th>Usuário</th>
              <th>Lote</th>
              <th>Posição</th>
              <th>Justificativa</th>
            </tr>
          </thead>
          <tbody>
            ${items.map(j => `
              <tr>
                <td>${new Date(j.created_at).toLocaleString('pt-BR')}</td>
                <td>${j.usuario_nome || '-'}</td>
                <td>${j.lote_numero || j.lote_id}</td>
                <td class="pos">${j.posicao_anterior ?? '—'} → ${j.posicao_nova}</td>
                <td>${j.justificativa}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `
    const win = window.open('', '_blank')
    win.document.write(conteudo)
    win.document.close()
    win.focus()
    win.print()
    win.close()
  }

  if (!isPremium) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-10 text-center max-w-md">
          <Lock size={40} className="mx-auto mb-3 text-yellow-500" />
          <h2 className="text-xl font-bold text-yellow-800 mb-2">Recurso Premium</h2>
          <p className="text-yellow-700 text-sm">
            O log de justificativas está disponível apenas no plano Premium com Filtros Automáticos habilitado.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Justificativas de Movimentação</h1>
          <p className="text-sm text-gray-500 mt-1">
            Log de todos os movimentos manuais com justificativa na ordem de entrada
          </p>
        </div>
        <button
          onClick={handleImprimir}
          disabled={items.length === 0}
          className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium text-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          <Printer size={16} />
          Imprimir
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Filter size={15} className="text-gray-400" />
          <span className="text-sm font-medium text-gray-600">Filtros:</span>
        </div>
        <select
          value={leilaoFiltro}
          onChange={e => { setLeilaoFiltro(e.target.value); setPage(1) }}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option value="">Todos os leilões</option>
          {leiloes.map(l => (
            <option key={l.id} value={l.id}>{l.nome}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-gray-400" />
          <input
            type="date"
            value={dataInicio}
            onChange={e => { setDataInicio(e.target.value); setPage(1) }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />
          <span className="text-gray-400 text-sm">até</span>
          <input
            type="date"
            value={dataFim}
            onChange={e => { setDataFim(e.target.value); setPage(1) }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
        {(leilaoFiltro || dataInicio || dataFim) && (
          <button
            onClick={() => { setLeilaoFiltro(''); setDataInicio(''); setDataFim(''); setPage(1) }}
            className="text-sm text-red-500 hover:underline"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {/* Tabela */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 h-16 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-16 text-center">
          <FileText size={36} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 font-medium">Nenhuma justificativa encontrada</p>
          <p className="text-gray-400 text-sm mt-1">Os movimentos manuais que exigem justificativa aparecerão aqui.</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Data/Hora</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Usuário</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Lote</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Posição</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Justificativa</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map(j => (
                  <tr key={j.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 text-xs">
                      {new Date(j.created_at).toLocaleDateString('pt-BR')}
                      <br />
                      <span className="text-gray-400">{new Date(j.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">{j.usuario_nome || '—'}</td>
                    <td className="px-4 py-3 font-mono text-gray-700">{j.lote_numero || j.lote_id}</td>
                    <td className="px-4 py-3 text-gray-600">
                      <span className="text-gray-400">{j.posicao_anterior ?? '—'}</span>
                      <span className="mx-1.5 text-gray-300">→</span>
                      <span className="font-semibold text-gray-800">{j.posicao_nova}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-xs">{j.justificativa}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-gray-500">
                {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} de {total} registros
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="text-sm text-gray-700 font-medium">{page} / {pages}</span>
                <button
                  onClick={() => setPage(p => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  className="p-2 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
